/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";

type Theme = "light" | "dark";

const LEGACY_THEME_STORAGE_KEY = "hotel-suite-theme";
const ADMIN_THEME_STORAGE_KEY = "hotel-suite-theme-admin";
const GUEST_THEME_STORAGE_KEY = "hotel-suite-theme-guest";

const isTheme = (value: string | null): value is Theme =>
  value === "light" || value === "dark";

const resolveThemeStorageKey = (pathname?: string): string => {
  const activePath =
    pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  return activePath.startsWith("/g/")
    ? GUEST_THEME_STORAGE_KEY
    : ADMIN_THEME_STORAGE_KEY;
};

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const storageKey = useMemo(() => resolveThemeStorageKey(), []);

  const [theme, setTheme] = useState<Theme>(() => {
    // Keep admin and guest themes isolated while still honoring the legacy key as fallback.
    const scopedTheme = localStorage.getItem(storageKey);
    if (isTheme(scopedTheme)) {
      return scopedTheme;
    }

    const legacyTheme = localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
    return isTheme(legacyTheme) ? legacyTheme : "light";
  });

  useEffect(() => {
    // Apply theme class to document root
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);

    // Also set data-theme attribute for backward compatibility
    document.documentElement.setAttribute("data-theme", theme);

    // Save to route-scoped localStorage key.
    localStorage.setItem(storageKey, theme);
  }, [storageKey, theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
